import { axiosBaseQuery } from '@api/api';
import { createApi } from '@reduxjs/toolkit/query/react';
import { AuthResponse } from '@store/api/auth/authApi';
import { OrgRole, ProjectAccessRole } from '@app-types/access';

export interface ProjectInviteLookup {
  project?: {
    projectId?: string;
    name?: string;
    slug?: string;
  };
  organization?: {
    orgId?: string;
    name?: string;
    slug?: string;
  };
  role?: ProjectAccessRole;
  expiresAt?: string;
  email?: string;
}

export interface OrgInviteLookup {
  organization?: {
    orgId?: string;
    name?: string;
    slug?: string;
  };
  role?: OrgRole;
  projects?: Array<{
    projectId?: string;
    name?: string;
    role?: ProjectAccessRole;
  }>;
  expiresAt?: string;
  email?: string;
}

export interface InviteAcceptBody {
  token: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

const toFormData = (body: InviteAcceptBody) => {
  const formData = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value);
    }
  });
  return formData;
};

export const invitesApi = createApi({
  reducerPath: 'invitesApi',
  baseQuery: axiosBaseQuery(),
  endpoints: builder => ({
    lookupProjectInvite: builder.query<ProjectInviteLookup, string>({
      query: token => ({
        url: '/project-invites/lookup',
        params: { token },
      }),
    }),
    acceptProjectInvite: builder.mutation<AuthResponse, InviteAcceptBody>({
      query: body => ({
        url: '/project-invites/accept',
        method: 'POST',
        data: toFormData(body),
      }),
    }),
    lookupOrgInvite: builder.query<OrgInviteLookup, string>({
      query: token => ({
        url: '/org-invites/lookup',
        params: { token },
      }),
    }),
    acceptOrgInvite: builder.mutation<AuthResponse, InviteAcceptBody>({
      query: body => ({
        url: '/org-invites/accept',
        method: 'POST',
        data: toFormData(body),
      }),
    }),
  }),
});

export const {
  useLookupProjectInviteQuery,
  useAcceptProjectInviteMutation,
  useLookupOrgInviteQuery,
  useAcceptOrgInviteMutation,
} = invitesApi;
